import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTablesContext } from "../../../context/tablesContextProvider";
import { CaseAppealListRow } from "../../../data-model/caseAppealsTable";
import { TableNames } from "../../../enums/tableNames";
import { RootState } from "../../../redux/store/store";
import { makePostCallForInitialDataFetch } from "../apis/makePostCallForInitialDataFetch";
import { buildAppealListPayload } from "../utils/buildAppealListPayload";
import { CaseAppealsTableState } from "./useCaseAppealsTableData";

export interface UseInitialDataFetchingForTableReturn {
  initialDataFetchingExecuted: boolean;
  setInitialDataFetchingExecuted: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Fetches the first page of appeal list data when the appeals table mounts.
 *
 * @param setCaseAppealsState - Table loading/error/data state setter
 * @param setTableData - Raw API rows setter
 * @param pageSize - Page size sent as `limit` to the appeal list API
 * @returns Initial fetch flag and setter used to trigger refresh
 */
export const useInitialDataFetchingForTable = (
  setCaseAppealsState: React.Dispatch<
    React.SetStateAction<CaseAppealsTableState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<CaseAppealListRow[]>>,
  pageSize: number,
): UseInitialDataFetchingForTableReturn => {
  const { setTableDataForContext, getTableDataFromContext } =
    useTablesContext();
  const [initialDataFetchingExecuted, setInitialDataFetchingExecuted] =
    useState<boolean>(false);

  const tableAttributes = getTableDataFromContext(
    TableNames.ONCOLOGY_CASE_APPEALS_ORDERS,
  );

  const { filters } = useSelector((state: RootState) => state.filterValues);

  const validFilters = useMemo(() => {
    return filters.filter(
      (filter) => filter.name === TableNames.ONCOLOGY_CASE_APPEALS_ORDERS,
    );
  }, [filters]);

  const setCaseAppealsStateRef = useRef(setCaseAppealsState);
  const setTableDataRef = useRef(setTableData);
  const setTableDataForContextRef = useRef(setTableDataForContext);

  useEffect(() => {
    setCaseAppealsStateRef.current = setCaseAppealsState;
    setTableDataRef.current = setTableData;
    setTableDataForContextRef.current = setTableDataForContext;
  }, [setCaseAppealsState, setTableData, setTableDataForContext]);

  useEffect(() => {
    if (initialDataFetchingExecuted) {
      return;
    }

    // Search text persisted for this table, so a remount reloads the same results.
    const searchedText = tableAttributes?.searchedText ?? "";
    const payload = buildAppealListPayload(
      validFilters,
      searchedText,
      pageSize,
    );

    makePostCallForInitialDataFetch(
      payload,
      setCaseAppealsStateRef.current,
      setTableDataRef.current,
    ).then(() => {
      setInitialDataFetchingExecuted(true);
      setTableDataForContextRef.current(
        TableNames.ONCOLOGY_CASE_APPEALS_ORDERS,
        {
          lastBatchFetched: 1,
        },
      );
    });
  }, [
    initialDataFetchingExecuted,
    pageSize,
    validFilters,
    tableAttributes?.searchedText,
  ]);

  return {
    initialDataFetchingExecuted,
    setInitialDataFetchingExecuted,
  };
};
